import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "lib/redux/store";
// import type { BackendJob } from "api/jobsApi";
import { DashboardJob } from "../../../types/dashboard";
// import type { DashboardJob } from "../../../../types/dashboard";

/**
 * Jobs Slice - Manages job listings state with pagination and filtering
 * 
 * State Management Strategy:
 * - Fetch all jobs once from backend on initial load
 * - Store in Redux for shared access across pages (dashboard, all-jobs)
 * - Apply filtering and pagination on frontend for better performance
 * - 10 jobs per page as per requirement
 */

// Job status types matching backend
export type JobStatus = "VIEWED" | "APPLIED" | "SAVED";

// Redux state shape
export interface JobsState {
  // All jobs fetched from backend
  allJobs: DashboardJob[];
  
  // Loading and error states
  isLoading: boolean;
  error: string | null;
  
  // Pagination state (frontend pagination)
  currentPage: number; // 1-based page numbering
  itemsPerPage: number; // Fixed at 10 per requirement
  
  // Filter state
  activeFilter: JobStatus | "ALL"; // Current tab filter
  
  // Sorting state
  sortOrder: "asc" | "desc" | null; // Date sort order (ascending/descending)
  
  // Last fetch timestamp for cache invalidation
  lastFetchedAt: number | null;
  
  // Fetch metadata
  totalJobsFromBackend: number;
}

// Initial state
export const initialJobsState: JobsState = {
  allJobs: [],
  isLoading: false,
  error: null,
  currentPage: 1,
  itemsPerPage: 10, // Fixed requirement
  activeFilter: "ALL",
  sortOrder: null, // No sorting by default
  lastFetchedAt: null,
  totalJobsFromBackend: 0,
};

/**
 * Jobs Slice
 * Follows project patterns from uiSlice.ts and resumeSlice.ts
 */
export const jobsSlice = createSlice({
  name: "jobs",
  initialState: initialJobsState,
  reducers: {
    // Set loading state
    setJobsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    // Set error state
    setJobsError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    // Store fetched jobs from backend
    setJobs: (state, action: PayloadAction<DashboardJob[]>) => {
      state.allJobs = action.payload;
      state.totalJobsFromBackend = action.payload.length;
      state.lastFetchedAt = Date.now();
      state.isLoading = false;
      state.error = null;
    },

    // Update a single job (for status changes, edits)
    updateJob: (state, action: PayloadAction<DashboardJob>) => {
      console.log("[Redux updateJob] Received update for job:", action.payload.id);
      console.log("[Redux updateJob] New job data:", action.payload);
      
      const index = state.allJobs.findIndex((job: DashboardJob) => job.id === action.payload.id);
      if (index !== -1) {
        console.log("[Redux updateJob] Found job at index:", index);
        console.log("[Redux updateJob] Old job data:", state.allJobs[index]);
        state.allJobs[index] = action.payload;
        console.log("[Redux updateJob] Job updated successfully in Redux store");
      } else {
        console.warn("[Redux updateJob] Job not found with id:", action.payload.id);
        console.warn("[Redux updateJob] Available job IDs:", state.allJobs.map(j => j.id));
      }
    },

    // Delete a job
    deleteJob: (state, action: PayloadAction<number>) => {
      state.allJobs = state.allJobs.filter((job: DashboardJob) => job.id !== action.payload);
      state.totalJobsFromBackend = state.allJobs.length;
    },

    // Delete multiple jobs
    deleteJobs: (state, action: PayloadAction<number[]>) => {
      const idsToDelete = new Set(action.payload);
      state.allJobs = state.allJobs.filter((job: DashboardJob) => !idsToDelete.has(job.id));
      state.totalJobsFromBackend = state.allJobs.length;
    },

    // Set active filter (tab)
    setActiveFilter: (state, action: PayloadAction<JobStatus | "ALL">) => {
      state.activeFilter = action.payload;
      state.currentPage = 1; // Reset to page 1 when filter changes
    },

    // Set sort order (date ascending/descending)
    setSortOrder: (state, action: PayloadAction<"asc" | "desc" | null>) => {
      state.sortOrder = action.payload;
      state.currentPage = 1; // Reset to page 1 when sort changes
    },

    // Toggle sort order (asc -> desc -> null -> asc)
    toggleSortOrder: (state) => {
      if (state.sortOrder === null) {
        state.sortOrder = "desc"; // Start with newest first
      } else if (state.sortOrder === "desc") {
        state.sortOrder = "asc"; // Then oldest first
      } else {
        state.sortOrder = null; // Then no sorting
      }
      state.currentPage = 1;
    },

    // Set current page
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },

    // Go to next page
    nextPage: (state) => {
      const filteredJobs = selectFilteredJobs({ jobs: state } as RootState);
      const totalPages = Math.ceil(filteredJobs.length / state.itemsPerPage);
      if (state.currentPage < totalPages) {
        state.currentPage += 1;
      }
    },

    // Go to previous page
    previousPage: (state) => {
      if (state.currentPage > 1) {
        state.currentPage -= 1;
      }
    },

    // Reset pagination to page 1
    resetPagination: (state) => {
      state.currentPage = 1;
    },

    // Clear all jobs (for logout or reset)
    clearJobs: (state) => {
      return initialJobsState;
    },
  },
});

// Export actions
export const {
  setJobsLoading,
  setJobsError,
  setJobs,
  updateJob,
  deleteJob,
  deleteJobs,
  setActiveFilter,
  setSortOrder,
  toggleSortOrder,
  setCurrentPage,
  nextPage,
  previousPage,
  resetPagination,
  clearJobs,
} = jobsSlice.actions;

/**
 * SELECTORS
 * Memoized selectors for efficient data access
 */

// Select all jobs
export const selectAllJobs = (state: RootState): DashboardJob[] => state.jobs.allJobs;

// Select loading state
export const selectJobsLoading = (state: RootState): boolean => state.jobs.isLoading;

// Select error state
export const selectJobsError = (state: RootState): string | null => state.jobs.error;

// Select active filter
export const selectActiveFilter = (state: RootState): JobStatus | "ALL" => state.jobs.activeFilter;

// Select sort order
export const selectSortOrder = (state: RootState): "asc" | "desc" | null => state.jobs.sortOrder;

// Select current page
export const selectCurrentPage = (state: RootState): number => state.jobs.currentPage;

// Select items per page
export const selectItemsPerPage = (state: RootState): number => state.jobs.itemsPerPage;

// Select last fetched timestamp
export const selectLastFetchedAt = (state: RootState): number | null => state.jobs.lastFetchedAt;

// Select total jobs count
export const selectTotalJobsCount = (state: RootState): number => state.jobs.totalJobsFromBackend;

/**
 * Select filtered jobs based on active filter and sort order
 * Returns jobs matching the current status filter, sorted by date if sortOrder is set
 */
export const selectFilteredJobs = (state: RootState): DashboardJob[] => {
  const { allJobs, activeFilter, sortOrder } = state.jobs;
  
  // Filter by status
  let filtered = activeFilter === "ALL" 
    ? allJobs 
    : allJobs.filter((job: DashboardJob) => job.status === activeFilter);
  
  // Sort by date if sortOrder is set
  if (sortOrder) {
    filtered = [...filtered].sort((a, b) => {
      // Use createdOn for sorting, fallback to dateApplied or current date
      const dateA = new Date(a.createdOn || a.dateApplied || Date.now()).getTime();
      const dateB = new Date(b.createdOn || b.dateApplied || Date.now()).getTime();
      
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });
  }
  
  return filtered;
};

/**
 * Select paginated jobs for current page
 * Returns 10 jobs (or fewer on last page) based on current page and filter
 */
export const selectPaginatedJobs = (state: RootState): DashboardJob[] => {
  const filteredJobs = selectFilteredJobs(state);
  const { currentPage, itemsPerPage } = state.jobs;
  
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  
  return filteredJobs.slice(startIndex, endIndex);
};

/**
 * Select pagination metadata
 * Returns current page, total pages, has prev/next, etc.
 */
export const selectPaginationInfo = (state: RootState) => {
  const filteredJobs = selectFilteredJobs(state);
  const { currentPage, itemsPerPage } = state.jobs;
  
  const totalItems = filteredJobs.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;
  
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);
  
  return {
    currentPage,
    totalPages,
    totalItems,
    itemsPerPage,
    hasNextPage,
    hasPrevPage,
    startItem,
    endItem,
  };
};

/**
 * Select jobs by status (helper for stats cards)
 * Returns count of jobs for each status
 */
export const selectJobsByStatus = (state: RootState) => {
  const allJobs = state.jobs.allJobs;
  
  return {
    viewed: allJobs.filter((job) => job.status === "VIEWED").length,
    applied: allJobs.filter((job) => job.status === "APPLIED").length,
    saved: allJobs.filter((job) => job.status === "SAVED").length,
    total: allJobs.length,
  };
};

/**
 * Check if jobs data is stale (> 5 minutes old)
 * Used to determine if we should refetch
 */
export const selectShouldRefetchJobs = (state: RootState): boolean => {
  const lastFetched = state.jobs.lastFetchedAt;
  if (!lastFetched) return true;
  
  const FIVE_MINUTES = 5 * 60 * 1000;
  return Date.now() - lastFetched > FIVE_MINUTES;
};

// Export reducer
export default jobsSlice.reducer;
